import React, { useState } from 'react';
import {
  Search, Filter, Download, ChevronLeft, ChevronRight,
  FileText, Calendar, TrendingUp, TrendingDown, DollarSign,
  ShoppingBag, Users, Package, Store, Star, Eye,
  BarChart3, PieChartIcon, LineChartIcon, Printer, Mail,
  Clock, CheckCircle, AlertCircle, Zap, Sparkles
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from 'recharts';
import { Link } from 'react-router-dom';

const PartnerReports = () => {
  const [dateRange, setDateRange] = useState('month');
  const [reportType, setReportType] = useState('sales');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Sales Report Data
  const salesData = [
    { month: 'Jan', revenue: 325000, orders: 245, profit: 210000 },
    { month: 'Feb', revenue: 412000, orders: 278, profit: 278000 },
    { month: 'Mar', revenue: 398000, orders: 312, profit: 265000 },
    { month: 'Apr', revenue: 456000, orders: 345, profit: 310000 },
    { month: 'May', revenue: 523000, orders: 389, profit: 367000 },
    { month: 'Jun', revenue: 498000, orders: 367, profit: 348000 },
    { month: 'Jul', revenue: 567000, orders: 412, profit: 397000 },
    { month: 'Aug', revenue: 589000, orders: 425, profit: 412000 },
    { month: 'Sep', revenue: 578000, orders: 418, profit: 404000 },
    { month: 'Oct', revenue: 612000, orders: 435, profit: 428000 },
    { month: 'Nov', revenue: 645000, orders: 448, profit: 451000 },
    { month: 'Dec', revenue: 720000, orders: 465, profit: 504000 },
  ];

  // Category Report Data
  const categoryData = [
    { name: 'Indoor Plants', value: 35, revenue: 1250000, color: '#10b981' },
    { name: 'Outdoor Plants', value: 25, revenue: 890000, color: '#3b82f6' },
    { name: 'Flowering Plants', value: 20, revenue: 720000, color: '#f59e0b' },
    { name: 'Air Purifying', value: 12, revenue: 430000, color: '#8b5cf6' },
    { name: 'Others', value: 8, revenue: 285000, color: '#ef4444' },
  ];

  // Top Sellers Report
  const topSellers = [
    { id: 1, name: 'Green Thumb Nursery', revenue: 458900, orders: 245, products: 45, rating: 4.9 },
    { id: 2, name: 'Flora Bliss', revenue: 325600, orders: 189, products: 32, rating: 4.8 },
    { id: 3, name: 'City Gardens', revenue: 245800, orders: 156, products: 28, rating: 4.7 },
    { id: 4, name: 'Herbals Co.', revenue: 187500, orders: 98, products: 22, rating: 4.6 },
    { id: 5, name: 'Succulent World', revenue: 145200, orders: 87, products: 18, rating: 4.8 },
  ];

  // Recent Report Downloads
  const recentReports = [
    { id: 'RPT-001', name: 'Monthly Sales Report', date: 'Oct 24, 2024', type: 'Sales', size: '2.4 MB', status: 'Generated' },
    { id: 'RPT-002', name: 'Category Performance', date: 'Oct 23, 2024', type: 'Category', size: '1.8 MB', status: 'Generated' },
    { id: 'RPT-003', name: 'Seller Analytics', date: 'Oct 22, 2024', type: 'Seller', size: '3.1 MB', status: 'Generated' },
    { id: 'RPT-004', name: 'Customer Insights', date: 'Oct 21, 2024', type: 'Customer', size: '2.2 MB', status: 'Generated' },
  ];

  const stats = {
    totalReports: 24,
    generatedThisMonth: 8,
    totalDownloads: 156,
    pendingReports: 3
  };

  const reportTabs = [
    { id: 'sales', label: 'Sales Report', icon: BarChart3 },
    { id: 'category', label: 'Category Report', icon: PieChartIcon },
    { id: 'seller', label: 'Seller Report', icon: Store },
    { id: 'customer', label: 'Customer Report', icon: Users },
  ];

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Generated': return { icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'Pending': return { icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      default: return { icon: AlertCircle, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  return (
    <div className="partner-reports-modern">
      {/* Header */}
      <div className="reports-header">
        <div>
          <h1 className="page-title">Reports & Analytics</h1>
          <p className="page-subtitle">Generate and download comprehensive business reports</p>
        </div>
        <div className="header-actions">
          <div className="date-range">
            <Calendar size={16} />
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last Quarter</option>
              <option value="year">This Year</option>
            </select>
          </div>
          <button className="btn-primary">
            <FileText size={16} />
            Generate Report
          </button>
        </div>
      </div>

      {/* Read-Only Banner */}
      <div className="readonly-banner">
        <Eye size={18} />
        <span>You have read-only access. You can view and download reports but cannot modify report settings.</span>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Reports" value={stats.totalReports} icon={FileText} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Generated This Month" value={stats.generatedThisMonth} icon={Calendar} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Total Downloads" value={stats.totalDownloads} icon={Download} color="#8b5cf6" bgColor="#e9d5ff" />
        <StatCard title="Pending Reports" value={stats.pendingReports} icon={Clock} color="#f59e0b" bgColor="#fed7aa" />
      </div>

      {/* Report Tabs */}
      <div className="report-tabs">
        {reportTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`tab-btn ${reportType === tab.id ? 'active' : ''}`}
              onClick={() => setReportType(tab.id)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Sales Report Tab */}
      {reportType === 'sales' && (
        <div className="report-content">
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h3>Sales Performance Report</h3>
                <p>Monthly revenue and order trends</p>
              </div>
              <div className="report-actions">
                <button className="btn-icon" title="Print">
                  <Printer size={16} />
                </button>
                <button className="btn-icon" title="Email">
                  <Mail size={16} />
                </button>
                <button className="btn-icon" title="Download">
                  <Download size={16} />
                </button>
              </div>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={salesData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8ecef" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(value) => `₹${value/1000}k`} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }}
                    formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-header">
              <h3>Sales Summary</h3>
            </div>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Total Revenue</span>
                <span className="summary-value">₹{salesData.reduce((sum, d) => sum + d.revenue, 0).toLocaleString()}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Orders</span>
                <span className="summary-value">{salesData.reduce((sum, d) => sum + d.orders, 0).toLocaleString()}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Average Order Value</span>
                <span className="summary-value">₹1,407</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Growth Rate</span>
                <span className="summary-value positive">+17%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Report Tab */}
      {reportType === 'category' && (
        <div className="report-content">
          <div className="two-column-grid">
            <div className="chart-card">
              <div className="chart-header">
                <h3>Category Distribution</h3>
                <p>Revenue by product category</p>
              </div>
              <div className="pie-chart-container">
                <ResponsiveContainer width="100%" height="250px">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="data-card">
              <div className="card-header">
                <h3>Category Performance</h3>
                <button className="btn-icon">
                  <Download size={16} />
                </button>
              </div>
              <div className="category-table">
                {categoryData.map((cat, idx) => (
                  <div key={idx} className="category-row">
                    <div className="category-info">
                      <span className="category-color" style={{ backgroundColor: cat.color }}></span>
                      <span className="category-name">{cat.name}</span>
                    </div>
                    <div className="category-numbers">
                      <span className="category-revenue">₹{cat.revenue.toLocaleString()}</span>
                      <span className="category-percentage">{cat.value}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seller Report Tab */}
      {reportType === 'seller' && (
        <div className="report-content">
          <div className="data-card full-width">
            <div className="card-header">
              <h3>Top Performing Sellers</h3>
              <div className="report-actions">
                <button className="btn-icon" title="Export">
                  <Download size={16} />
                </button>
              </div>
            </div>
            <div className="table-container">
              <div className="table-responsive">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Seller Name</th>
                      <th>Revenue</th>
                      <th>Orders</th>
                      <th>Products</th>
                      <th>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSellers.map((seller, idx) => (
                      <tr key={seller.id}>
                        <td className="rank">#{idx + 1}</td>
                        <td className="seller-name">{seller.name}</td>
                        <td className="revenue">₹{seller.revenue.toLocaleString()}</td>
                        <td>{seller.orders}</td>
                        <td>{seller.products}</td>
                        <td className="rating">
                          <Star size={12} fill="#f59e0b" color="#f59e0b" />
                          <span>{seller.rating}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Report Tab */}
      {reportType === 'customer' && (
        <div className="report-content">
          <div className="data-card full-width">
            <div className="card-header">
              <h3>Customer Insights Report</h3>
              <div className="report-actions">
                <button className="btn-icon" title="Export">
                  <Download size={16} />
                </button>
              </div>
            </div>
            <div className="stats-mini-grid">
              <div className="stat-mini">
                <Users size={20} color="#10b981" />
                <div>
                  <h4>8,450</h4>
                  <span>Total Customers</span>
                </div>
              </div>
              <div className="stat-mini">
                <TrendingUp size={20} color="#3b82f6" />
                <div>
                  <h4>27.9%</h4>
                  <span>Growth Rate</span>
                </div>
              </div>
              <div className="stat-mini">
                <ShoppingBag size={20} color="#f59e0b" />
                <div>
                  <h4>₹1,407</h4>
                  <span>Avg Order Value</span>
                </div>
              </div>
              <div className="stat-mini">
                <Star size={20} color="#8b5cf6" />
                <div>
                  <h4>4.7</h4>
                  <span>Avg Rating</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Reports */}
      <div className="data-card">
        <div className="card-header">
          <h3>Recent Reports</h3>
          <Link to="/partner/reports/all" className="view-link">View All →</Link>
        </div>
        <div className="table-container">
          <div className="table-responsive">
            <table className="reports-list-table">
              <thead>
                <tr>
                  <th>Report Name</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Size</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentReports.map((report) => {
                  const statusConfig = getStatusConfig(report.status);
                  const StatusIcon = statusConfig.icon;
                  return (
                    <tr key={report.id}>
                      <td className="report-name">{report.name}</td>
                      <td className="report-type">{report.type}</td>
                      <td className="report-date">{report.date}</td>
                      <td className="report-size">{report.size}</td>
                      <td className="report-status">
                        <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                          <StatusIcon size={12} />
                          {report.status}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="action-buttons">
                          <button className="action-btn" title="Download">
                            <Download size={16} />
                          </button>
                          <button className="action-btn" title="View">
                            <Eye size={16} />
                          </button>
                        </div>
                       </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="insights-card">
        <div className="insights-header">
          <Zap size={20} />
          <h3>Report Insights</h3>
        </div>
        <div className="insights-grid">
          <div className="insight-item">
            <div className="insight-icon">📈</div>
            <div>
              <h4>Sales Growth Trend</h4>
              <p>Revenue increased by 17% this month. Q4 shows strongest performance.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">🏆</div>
            <div>
              <h4>Top Performing Category</h4>
              <p>Indoor Plants leads with 35% of total revenue.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">👑</div>
            <div>
              <h4>Best Seller</h4>
              <p>Green Thumb Nursery is top performer with ₹4.58L revenue.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">📊</div>
            <div>
              <h4>Report Usage</h4>
              <p>Sales reports are downloaded most frequently (45% of all downloads).</p>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .partner-reports-modern {
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }

        /* Header */
        .reports-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 4px;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .date-range {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
        }

        .date-range select {
          border: none;
          background: transparent;
          font-size: 13px;
          cursor: pointer;
        }

        .btn-primary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          border-radius: 40px;
          color: white;
          font-size: 13px;
          cursor: pointer;
        }

        /* Read-Only Banner */
        .readonly-banner {
          background: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 12px;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          color: #92400e;
        }

        /* Stats Cards */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: white;
          border-radius: 20px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          border: 1px solid #e8ecef;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
        }

        .stat-info h3 {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .stat-info span {
          font-size: 11px;
          color: #64748b;
        }

        /* Report Tabs */
        .report-tabs {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
          background: white;
          padding: 8px;
          border-radius: 60px;
          width: fit-content;
          border: 1px solid #e8ecef;
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 24px;
          background: transparent;
          border: none;
          border-radius: 40px;
          font-size: 13px;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
        }

        .tab-btn.active {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        /* Report Content */
        .report-content {
          display: flex;
          flex-direction: column;
          gap: 24px;
          margin-bottom: 24px;
        }

        .chart-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          border: 1px solid #e8ecef;
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .chart-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .chart-header p {
          font-size: 12px;
          color: #94a3b8;
          margin: 2px 0 0;
        }

        .report-actions {
          display: flex;
          gap: 8px;
        }

        .btn-icon {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }

        .btn-icon:hover {
          background: #e2e8f0;
        }

        .chart-container {
          height: 320px;
        }

        .summary-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          border: 1px solid #e8ecef;
        }

        .summary-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 16px;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .summary-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .summary-label {
          font-size: 11px;
          color: #64748b;
        }

        .summary-value {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
        }

        .summary-value.positive {
          color: #10b981;
        }

        /* Two Column Grid */
        .two-column-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .pie-chart-container {
          display: flex;
          justify-content: center;
        }

        .data-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          border: 1px solid #e8ecef;
        }

        .data-card.full-width {
          grid-column: span 2;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .card-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .view-link {
          font-size: 13px;
          color: #10b981;
          text-decoration: none;
        }

        /* Category Table */
        .category-table {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .category-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .category-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .category-color {
          width: 10px;
          height: 10px;
          border-radius: 3px;
        }

        .category-name {
          font-size: 13px;
          color: #475569;
        }

        .category-numbers {
          display: flex;
          gap: 16px;
        }

        .category-revenue {
          font-weight: 600;
          color: #10b981;
        }

        .category-percentage {
          color: #64748b;
        }

        /* Stats Mini Grid */
        .stats-mini-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .stat-mini {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: #f8fafc;
          border-radius: 16px;
        }

        .stat-mini h4 {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .stat-mini span {
          font-size: 11px;
          color: #64748b;
        }

        /* Tables */
        .table-container {
          overflow-x: auto;
        }

        .reports-table, .reports-list-table {
          width: 100%;
          border-collapse: collapse;
        }

        .reports-table th, .reports-list-table th {
          text-align: left;
          padding: 12px;
          background: #f8fafc;
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          border-bottom: 1px solid #e8ecef;
        }

        .reports-table td, .reports-list-table td {
          padding: 12px;
          border-bottom: 1px solid #e8ecef;
          font-size: 13px;
        }

        .rank {
          font-weight: 700;
          color: #f59e0b;
        }

        .revenue {
          font-weight: 600;
          color: #10b981;
        }

        .rating {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .action-btn {
          width: 28px;
          height: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }

        .action-btn:hover {
          background: #e2e8f0;
        }

        .text-right {
          text-align: right;
        }

        /* Insights Card */
        .insights-card {
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          border-radius: 20px;
          padding: 24px;
          margin-top: 8px;
        }

        .insights-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 20px;
        }

        .insights-header svg {
          color: #f59e0b;
        }

        .insights-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .insights-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .insight-item {
          display: flex;
          gap: 14px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          backdrop-filter: blur(10px);
        }

        .insight-icon {
          font-size: 28px;
        }

        .insight-item h4 {
          font-size: 13px;
          font-weight: 600;
          color: white;
          margin: 0 0 4px;
        }

        .insight-item p {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.8);
          margin: 0;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .stats-grid, .summary-grid, .stats-mini-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .two-column-grid {
            grid-template-columns: 1fr;
          }
          .insights-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .partner-reports-modern {
            padding: 16px;
          }
          .report-tabs {
            flex-wrap: wrap;
            width: 100%;
          }
          .tab-btn {
            flex: 1;
            justify-content: center;
          }
          .stats-grid {
            grid-template-columns: 1fr;
          }
          .summary-grid, .stats-mini-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default PartnerReports;